import type { ClientToServer, ServerToClient, ConversationMessage, OwuiChatSummary, ChangeProposalState } from "@lupp/shared";
import { getSocket } from "./socket";

export interface LoadedChat {
  messages: ConversationMessage[]; // ALL tree nodes (with parentId/childrenIds)
  currentId: string | null; // active leaf
  title: string; // OWUI chat title (for the topbar breadcrumb)
}

/** Lädt die volle History eines OWUI-Chats (Baum + aktives Blatt) über das Backend. */
export async function loadChat(chatId: string): Promise<LoadedChat> {
  const socket = getSocket();

  return new Promise<LoadedChat>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "chat_loaded" && event.chatId === chatId) {
        cleanup();
        resolve({ messages: event.history, currentId: event.currentId ?? null, title: event.title ?? "" });
      } else if (event.type === "auth_required") {
        cleanup();
        reject(new Error("auth required"));
      } else if (event.type === "error") {
        cleanup();
        reject(new Error(event.message));
      }
    };

    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };

    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
    socket.emit("chat_load", { type: "chat_load", chatId } satisfies ClientToServer);
  });
}

/** Abonniert `chat_saved` (frischer Baum nach Fork-Turn). Liefert eine Unsubscribe-Funktion. */
export function subscribeChatSaved(
  onSaved: (e: { chatId: string; messages: ConversationMessage[]; currentId: string | null }) => void,
): () => void {
  const socket = getSocket();
  const onEvent = (event: ServerToClient): void => {
    if (event.type === "chat_saved") {
      onSaved({ chatId: event.chatId, messages: event.history, currentId: event.currentId ?? null });
    }
  };
  socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
  return () => socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
}

/** Verschiebt einen Chat in einen Ordner (folderId=null ⇒ oberste Ebene). Resolved auf den Ack. */
export async function moveChat(chatId: string, folderId: string | null): Promise<void> {
  const socket = getSocket();
  return new Promise<void>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "chat_moved" && event.chatId === chatId) {
        cleanup();
        resolve();
      } else if (event.type === "error") {
        cleanup();
        reject(new Error(event.message));
      }
    };
    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };
    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
    socket.emit("chat_move_folder", { type: "chat_move_folder", chatId, folderId } satisfies ClientToServer);
  });
}

/** Abonniert `chat_title` (neuer Chat: generierter Titel persistiert). Unsubscribe-Funktion. */
export function subscribeChatTitle(onTitled: (e: { chatId: string; title: string }) => void): () => void {
  const socket = getSocket();
  const onEvent = (event: ServerToClient): void => {
    if (event.type === "chat_title") onTitled({ chatId: event.chatId, title: event.title });
  };
  socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
  return () => socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
}

/** Ändert den Text einer bestehenden Chat-Nachricht (Edit „Speichern", kein Branch). Fire-and-forget. */
export function editMessage(chatId: string, messageId: string, content: string): void {
  getSocket().emit("edit_message", { type: "edit_message", chatId, messageId, content } satisfies ClientToServer);
}

/**
 * Persistiert den Auflösungs-Status der Änderungsvorschläge einer Assistant-Nachricht
 * (übernommen/abgelehnt + Apply-Option), adressiert über die echte Message-ID. Fire-and-forget.
 */
export function persistChangeStates(chatId: string, messageId: string, changeStates: Record<string, ChangeProposalState>): void {
  getSocket().emit("update_change_states", { type: "update_change_states", chatId, messageId, changeStates } satisfies ClientToServer);
}

/** Echte Assistant-Message-ID nach dem Persistieren eines normalen Turns (ID-Übernahme). */
export function subscribeTurnPersisted(onPersisted: (e: { chatId: string; messageId: string }) => void): () => void {
  const socket = getSocket();
  const onEvent = (event: ServerToClient): void => {
    if (event.type === "turn_persisted") onPersisted({ chatId: event.chatId, messageId: event.messageId });
  };
  socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
  return () => socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
}

/** Löscht eine Nachricht + ihren Teilbaum (Verlauf endet am Parent). Fire-and-forget. */
export function deleteMessage(chatId: string, messageId: string): void {
  getSocket().emit("delete_message", { type: "delete_message", chatId, messageId } satisfies ClientToServer);
}

/** Listet die OWUI-Chats des Users (über das Backend). */
export async function listChats(): Promise<OwuiChatSummary[]> {
  const socket = getSocket();

  return new Promise<OwuiChatSummary[]>((resolve, reject) => {
    const onEvent = (event: ServerToClient): void => {
      if (event.type === "chat_list_result") {
        cleanup();
        resolve(event.chats);
      } else if (event.type === "auth_required") {
        cleanup();
        reject(new Error("auth required"));
      } else if (event.type === "error") {
        cleanup();
        reject(new Error(event.message));
      }
    };

    const cleanup = (): void => {
      socket.off("event", onEvent as Parameters<typeof socket.off>[1]);
    };

    socket.on("event", onEvent as Parameters<typeof socket.on>[1]);
    socket.emit("chat_list", { type: "chat_list" } satisfies ClientToServer);
  });
}
